import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { BenefitType, Box } from 'edenred-ui';

import { FormFields } from '@epi-forms/helpers';
import { FundsLoadingTypes } from '@epi-constants/benefitTypes';
import { getNumber } from '@epi-helpers/numeral';
import { getBenefitSelected } from '@epi-selectors/benefitGroupSettings';
import { IAdminUpdateFooter } from '@epi-models/pages/TransferPage';
import { isEmptyObj } from '@epi-helpers/isEmptyObj';
import { TopupFormStatusesType } from '@epi-constants/topupForm';
import { getCurrentLunchFundsLoadingType } from '@epi-selectors/settings';
import { getApplicationLanguageId } from '@epi-store/application/application.selectors';
import {
  BenefitSelection,
  LoadAmount,
  LoadEmployeesForm
} from '@epi-pages/LoadBenefit/components/shared';
import {
  useTopupEmployees,
  useAdminTopupRequest,
  useTopupSwitch
} from '../../../hooks';
import { TopupInProgress } from '../../Topup/TopupForm/TopupInProgress';
import { TopupConfirmationModal } from '../../Topup/TopupForm/TopupConfirmationModal';
import { getTopupInProgressRequest } from '@epi-api/topupLoad';
import { showNotification } from '@epi-actions/overlays';

import {
  adminAmountValidators,
  adminWorkingDaysValidators
} from '../adminTopupValidators';
import { AdminScheduleInfo } from '../AdminScheduleInfo';

export const AdminTopupForm = ({
  updateFooter,
  form,
  loadableBenefits
}: IAdminUpdateFooter) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showTopupForm, setShowTopupForm] = useState(true);
  const [anotherTopupCheckPending, setAnotherTopupCheckPending] =
    useState<boolean>(false);
  const [topupRequestStatus, setTopupRequestStatus] =
    useState<TopupFormStatusesType | null>(null);

  const language = useSelector(getApplicationLanguageId);

  const {
    watch,
    handleSubmit,
    setValue,
    resetField,
    getValues,
    formState: { isValid, errors }
  } = form;

  const benefitType = watch(FormFields.Benefit);
  const benefitGroupIds = watch(FormFields.BenefitGroupIds);
  const topupPerPerson = watch(FormFields.MoneyAmount);
  const daysToTopup = watch(FormFields.DaysForLunari);
  const benefitSettings = useSelector(state =>
    getBenefitSelected(state, benefitType)
  );

  const currentLunchFundsLoadingType = useSelector(
    getCurrentLunchFundsLoadingType
  );

  const {
    paginationCount,
    toggleEmployee,
    checkedCount,
    lowestYearlyLimit,
    topupBeneficiariesIds,
    toggleAllEmployee,
    isAllEmployeeChecked,
    setForceUpdate,
    formattedTopup,
    formattedFee,
    formattedVat,
    formattedTopupWithFees,
    formattedRemainingBalance,
    remainingBalance,
    fee,
    vat,
    employeesToTopup,
    checkableEmployees
  } = useTopupEmployees();

  const { handleToggler, isToggle, showOnHoldBtn } = useTopupSwitch();

  const {
    postAdminTopupRequest,
    isTopupPending,
    isTopupProcessing,
    summaryId
  } = useAdminTopupRequest(
    setShowConfirmationModal,
    isToggle,
    showOnHoldBtn,
    setTopupRequestStatus
  );

  const isBatchOrder = checkedCount > 50;

  const isPaymentTypeTopUpRequest = checkableEmployees.length
    ? checkableEmployees.some(
        ({ fundsLoadingType }) =>
          fundsLoadingType === FundsLoadingTypes.TopUpRequest
      )
    : currentLunchFundsLoadingType === FundsLoadingTypes.TopUpRequest;

  const isLunchTopupRequest =
    isPaymentTypeTopUpRequest && benefitType === BenefitType.lunch;

  const onSubmit = async () => {
    setAnotherTopupCheckPending(true);
    const isTopupInProgress = await getTopupInProgressRequest();
    if (isTopupInProgress.isTopUpLoadInProgress) {
      dispatch(
        showNotification(
          { message: `messages.another_top_up_load_in_progress` },
          'error'
        )
      );
    } else {
      setShowConfirmationModal(true);
    }
    setAnotherTopupCheckPending(false);
  };

  const handleCloseModal = () => setShowConfirmationModal(false);

  const handleTopupRequest = () => {
    const isLunch = benefitType === BenefitType.lunch;
    const payload = {
      amount:
        !isLunch && topupPerPerson
          ? (getNumber(topupPerPerson) * 100).toFixed(0)
          : 0,
      numberOfWorkingDays: isLunch
        ? Number(getValues(FormFields.DaysForLunari))
        : 0,
      benefitType,
      beneficiariesIds: topupBeneficiariesIds
    };
    postAdminTopupRequest(payload);
    resetField(FormFields.MoneyAmount);
    resetField(FormFields.Benefit);
    resetField(FormFields.BenefitGroupIds);
  };

  const handleResetTopupStatus = () => {
    setTopupRequestStatus(null);
  };

  const handleShowTopupForm = (show: boolean) => {
    if (show) {
      setTopupRequestStatus(null);
    }
    setShowTopupForm(show);
  };

  useEffect(() => {
    if (loadableBenefits?.length === 1) {
      setValue(FormFields.Benefit, loadableBenefits[0]);
    }
  }, [summaryId, showTopupForm]);

  useEffect(() => {
    if (isTopupProcessing || isTopupPending) {
      setShowTopupForm(false);
    }
  }, [isTopupProcessing, isTopupPending]);

  useEffect(() => {
    if (showTopupForm) {
      updateFooter({
        submitText: t('containers.deposit_money.topup_tab.footer_button'),
        onSubmit: handleSubmit(onSubmit),
        disableSubmit:
          anotherTopupCheckPending ||
          !isValid ||
          !isEmptyObj(errors) ||
          !topupBeneficiariesIds.length ||
          !checkedCount ||
          (benefitType === BenefitType.lunch &&
            (!daysToTopup || daysToTopup === 0))
      });
    }
    if (!showTopupForm) {
      updateFooter(undefined);
    }
  }, [
    showTopupForm,
    isValid,
    topupBeneficiariesIds.length,
    isEmptyObj(errors),
    benefitType,
    employeesToTopup,
    language,
    daysToTopup,
    benefitGroupIds,
    anotherTopupCheckPending,
    checkedCount
  ]);

  if (!showTopupForm) {
    return (
      <TopupInProgress
        setShowTopupForm={handleShowTopupForm}
        isTopupProcessing={isTopupProcessing || isTopupPending}
        summaryId={summaryId}
        loadableBenefits={loadableBenefits}
        setForceUpdate={setForceUpdate}
      />
    );
  }

  return (
    <Box>
      <BenefitSelection loadableBenefits={loadableBenefits} stepNumber={1} />
      <AdminScheduleInfo stepNumber={2} />
      <LoadAmount
        stepNumber={3}
        daysExceed={false}
        daysToTopup={daysToTopup}
        remainingBalance={remainingBalance}
        benefitType={benefitType}
        lowestYearlyLimit={lowestYearlyLimit}
        benefitSettings={benefitSettings}
        workingDaysValidators={adminWorkingDaysValidators}
        showMaxWorkingDaysInfo={false}
        showOverBalanceWarning={false}
      />
      <LoadEmployeesForm
        stepNumber={4}
        isLunchTopupRequest={isLunchTopupRequest}
        formattedTopup={formattedTopup}
        formattedVat={formattedVat}
        paginationCount={paginationCount}
        toggleEmployee={toggleEmployee}
        toggleAllEmployee={toggleAllEmployee}
        checkedCount={checkedCount}
        isAllEmployeeChecked={isAllEmployeeChecked}
        formattedFee={formattedFee}
        formattedTopupWithFees={formattedTopupWithFees}
        formattedRemainingBalance={formattedRemainingBalance}
        remainingBalance={remainingBalance}
        fee={fee}
        vat={vat}
        handleToggler={handleToggler}
        isToggle={isToggle}
        showOnHoldBtn={showOnHoldBtn}
        showRemainingBalance={false}
      />
      <TopupConfirmationModal
        showModal={showConfirmationModal}
        employeesCount={checkedCount}
        handleCloseModal={handleCloseModal}
        handleTopupRequest={handleTopupRequest}
        totalAmount={formattedTopupWithFees}
        isTopupPending={isTopupPending}
        showOnHoldText={isToggle}
        topupAmount={formattedTopup}
        isPendingBenefit={false}
        showOnboardingContent={
          (isTopupPending || !!topupRequestStatus) &&
          (!showTopupForm || isTopupPending || !!topupRequestStatus)
        }
        topupRequestStatus={topupRequestStatus}
        resetTopupStatus={handleResetTopupStatus}
        isBatchOrder={isBatchOrder}
        isAdminTopup
      />
    </Box>
  );
};

AdminTopupForm.displayName = 'AdminTopupForm';
